import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/SEO';
import { getProductById, getRelatedProducts } from '@/mocks/products';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const siteUrl = import.meta.env.VITE_SITE_URL || '';

function buildProductJsonLd(product: ReturnType<typeof getProductById>) {
  if (!product) return undefined;
  return {
    '@type': 'Product',
    name: product.name,
    image: product.image,
    description: product.description,
    brand: {
      '@type': 'Brand',
      name: 'Hamva Pharmaceuticals',
    },
    manufacturer: {
      '@type': 'Organization',
      name: 'Hamva Pharmaceuticals (Pvt.) Ltd.',
    },
    category: product.category,
    offers: product.sizes.map((size) => ({
      '@type': 'Offer',
      url: `${siteUrl}/product/${product.id}`,
      availability: 'https://schema.org/InStock',
      itemOffered: {
        '@type': 'Product',
        name: `${product.name} - ${size}`,
      },
    })),
    targetAnimal: product.targetAnimals,
  };
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);
  const product = getProductById(productId);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const { ref, isVisible } = useScrollAnimation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [productId]);

  useEffect(() => {
    if (product && product.sizes.length > 0) {
      setSelectedSize(product.sizes[0]);
    }
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-dark font-inter text-white flex flex-col">
        <SEO
          title="Product Not Found | Hamva Pharmaceuticals"
          description="The product you are looking for does not exist in our catalog. Browse our full range of veterinary products."
          noindex
        />
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/5 mx-auto mb-6">
              <i className="ri-error-warning-line text-4xl text-accent" />
            </div>
            <h1 className="font-poppins font-bold text-3xl text-white mb-3">Product Not Found</h1>
            <p className="text-text-muted mb-8">The product you are looking for does not exist.</p>
            <Link
              to="/products"
              className="wa-btn inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Back to Products
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Layout flags
  const showMechanism = Boolean(product.mechanismOfAction);
  const showWhyDifferent = Boolean(product.whyDifferent);
  const showPerfectFor = Boolean(product.perfectFor?.length);

  const related = getRelatedProducts(product.id, product.category);

  return (
    <div className="min-h-screen bg-dark font-inter text-white">
      <SEO
        title={`${product.name} | Hamva Pharmaceuticals - ${product.category}`}
        description={product.benefit}
        keywords={`${product.name.toLowerCase()}, ${product.category.toLowerCase()}, veterinary ${product.category.toLowerCase()}, livestock supplement pakistan, hamva pharmaceuticals`}
        ogTitle={`${product.name} | Hamva Pharmaceuticals`}
        ogDescription={product.description}
        ogImage={product.image}
        ogType="product"
        canonical={`${siteUrl}/product/${product.id}`}
        jsonLd={buildProductJsonLd(product)}
      />
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-28 pb-4 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-text-muted hover:text-white transition-colors cursor-pointer whitespace-nowrap">Home</Link>
            <i className="ri-arrow-right-s-line text-white/20 text-xs" />
            <Link to="/products" className="text-text-muted hover:text-white transition-colors cursor-pointer whitespace-nowrap">Products</Link>
            <i className="ri-arrow-right-s-line text-white/20 text-xs" />
            <span className="text-accent whitespace-nowrap">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Main Product Section */}
      <section className="py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Product Image */}
            <div className="relative">
              <div
                className="rounded-2xl overflow-hidden flex items-center justify-center"
                style={{ background: '#F5F5F0', minHeight: '480px' }}
              >
                <img
                  src={product.fullSizeImage}
                  alt={product.name}
                  className="w-full h-full object-contain p-6 md:p-10"
                  style={{ maxHeight: '520px' }}
                />
              </div>
              {product.badge && (
                <div className="absolute top-4 left-4 bg-accent px-4 py-1.5 rounded-full text-xs font-semibold text-white whitespace-nowrap">
                  {product.badge}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="flex flex-col">
              <p className="text-accent text-xs font-semibold tracking-[2px] uppercase mb-3">
                {product.category}
              </p>
              <h1 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4 leading-tight">
                {product.name}
              </h1>
              <p className="text-text-muted text-base leading-relaxed mb-6">
                {product.description}
              </p>

              {/* Key Benefit Highlight */}
              <div
                className="rounded-xl p-5 mb-6 border border-white/5"
                style={{ background: 'rgba(196,30,58,0.08)' }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/20 flex-shrink-0">
                    <i className="ri-shield-check-line text-accent" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm mb-1">Key Benefit</p>
                    <p className="text-text-muted text-sm">{product.benefit}</p>
                  </div>
                </div>
              </div>

              {/* Size Selector */}
              <div className="mb-6">
                <p className="text-white font-semibold text-sm mb-3">Available Presentations</p>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                        selectedSize === size
                          ? 'bg-accent border-accent text-white'
                          : 'border-white/10 text-text-muted hover:border-accent/40 hover:text-white'
                      }`}
                      style={{ background: selectedSize === size ? undefined : '#131923' }}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Animals */}
              <div className="mb-8">
                <p className="text-white font-semibold text-sm mb-3">Suitable For</p>
                <div className="flex flex-wrap gap-2">
                  {product.targetAnimals.map((animal) => (
                    <span
                      key={animal}
                      className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border border-white/10"
                      style={{ background: '#131923', color: '#8A8FA8' }}
                    >
                      <i className="ri-heart-3-line mr-1.5 text-accent" />
                      {animal}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <a
                href={`https://wa.me/923007321011?text=Hi%20Hamva%2C%20I%20am%20interested%20in%20${encodeURIComponent(product.name)}%20(${encodeURIComponent(selectedSize || product.sizes[0])})`}
                target="_blank"
                rel="noopener noreferrer"
                className="wa-btn flex items-center justify-center gap-3 w-full sm:w-auto px-8 py-4 rounded-xl text-base font-semibold text-white cursor-pointer whitespace-nowrap red-glow"
              >
                <i className="ri-whatsapp-line text-xl" />
                Order on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="section-divider" />

      {/* Details Section */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Why This Product is Different — full-width highlight */}
          {showWhyDifferent && (
            <div
              className="rounded-2xl p-6 md:p-8 border border-accent/20 mb-8"
              style={{ background: 'rgba(196,30,58,0.08)' }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent/20 flex-shrink-0">
                  <i className="ri-sparkling-line text-accent text-xl" />
                </div>
                <div>
                  <h3 className="font-poppins font-bold text-lg text-white mb-2">Why This Product is Different</h3>
                  <p className="text-text-muted text-sm leading-relaxed">{product.whyDifferent}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Composition */}
            <div
              className="rounded-2xl p-6 md:p-8 border border-white/5"
              style={{ background: '#131923' }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/15">
                  <i className="ri-flask-line text-accent text-lg" />
                </div>
                <div>
                  <h3 className="font-poppins font-bold text-lg text-white">Composition</h3>
                  <p className="text-text-muted text-xs mt-0.5">{product.compositionNote}</p>
                </div>
              </div>
              <div className="space-y-0 divide-y divide-white/5">
                {product.composition.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5">
                    <span className="text-text-muted text-sm">{item.name}</span>
                    <span className="text-white text-sm font-semibold ml-4 whitespace-nowrap">{item.amount}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Benefits */}
            <div
              className="rounded-2xl p-6 md:p-8 border border-white/5"
              style={{ background: '#131923' }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/15">
                  <i className="ri-star-line text-accent text-lg" />
                </div>
                <h3 className="font-poppins font-bold text-lg text-white">Key Benefits</h3>
              </div>
              <ul className="space-y-3">
                {product.keyBenefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent/20 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-accent text-xs" />
                    </div>
                    <span className="text-text-muted text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Indications */}
            <div
              className="rounded-2xl p-6 md:p-8 border border-white/5"
              style={{ background: '#131923' }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/15">
                  <i className="ri-stethoscope-line text-accent text-lg" />
                </div>
                <h3 className="font-poppins font-bold text-lg text-white">Indications</h3>
              </div>
              <ul className="space-y-3">
                {product.indications.map((indication, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white/5 flex-shrink-0 mt-0.5">
                      <i className="ri-arrow-right-s-line text-accent text-xs" />
                    </div>
                    <span className="text-text-muted text-sm">{indication}</span>
                  </li>
                ))}
              </ul>
            </div>

            {showPerfectFor ? (
              /* Perfect For */
              <div
                className="rounded-2xl p-6 md:p-8 border border-white/5"
                style={{ background: '#131923' }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/15">
                    <i className="ri-thumb-up-line text-accent text-lg" />
                  </div>
                  <h3 className="font-poppins font-bold text-lg text-white">Perfect For</h3>
                </div>
                <ul className="space-y-3">
                  {product.perfectFor!.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent/20 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-accent text-xs" />
                      </div>
                      <span className="text-text-muted text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-5 border-t border-white/5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-text-muted text-xs">Category</span>
                    <span className="text-white text-xs font-medium">{product.category}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted text-xs">Manufacturer</span>
                    <span className="text-white text-xs font-medium">Hamva Pharmaceuticals (Pvt.) Ltd.</span>
                  </div>
                </div>
              </div>
            ) : showMechanism ? (
              /* Mechanism of Action */
              <div
                className="rounded-2xl p-6 md:p-8 border border-white/5"
                style={{ background: '#131923' }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/15">
                    <i className="ri-lightbulb-flash-line text-accent text-lg" />
                  </div>
                  <h3 className="font-poppins font-bold text-lg text-white">Mechanism of Action</h3>
                </div>
                <p className="text-text-muted text-sm leading-relaxed mb-5">
                  {product.mechanismOfAction}
                </p>
                <div className="rounded-lg p-4 border border-white/5" style={{ background: 'rgba(196,30,58,0.06)' }}>
                  <div className="flex items-start gap-2">
                    <i className="ri-information-line text-accent text-sm mt-0.5 flex-shrink-0" />
                    <p className="text-text-muted text-xs leading-relaxed">
                      Always consult a veterinarian before starting any new supplement regimen. Store in a cool, dry place below 25°C away from direct sunlight. Keep away from children.
                    </p>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-white/5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-text-muted text-xs">Category</span>
                    <span className="text-white text-xs font-medium">{product.category}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted text-xs">Manufacturer</span>
                    <span className="text-white text-xs font-medium">Hamva Pharmaceuticals (Pvt.) Ltd.</span>
                  </div>
                </div>
              </div>
            ) : (
              /* Dosage & Usage */
              <div
                className="rounded-2xl p-6 md:p-8 border border-white/5"
                style={{ background: '#131923' }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/15">
                    <i className="ri-file-list-3-line text-accent text-lg" />
                  </div>
                  <h3 className="font-poppins font-bold text-lg text-white">Dosage & Usage</h3>
                </div>
                <p className="text-text-muted text-sm leading-relaxed mb-5">
                  {product.dosage}
                </p>
                <div className="rounded-lg p-4 border border-white/5" style={{ background: 'rgba(196,30,58,0.06)' }}>
                  <div className="flex items-start gap-2">
                    <i className="ri-information-line text-accent text-sm mt-0.5 flex-shrink-0" />
                    <p className="text-text-muted text-xs leading-relaxed">
                      Always consult a veterinarian before starting any new supplement regimen. Store in a cool, dry place below 25°C away from direct sunlight. Keep away from children.
                    </p>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-white/5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-text-muted text-xs">Category</span>
                    <span className="text-white text-xs font-medium">{product.category}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted text-xs">Manufacturer</span>
                    <span className="text-white text-xs font-medium">Hamva Pharmaceuticals (Pvt.) Ltd.</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Full-width dosage when mechanism or perfectFor card is shown */}
          {(showMechanism || showPerfectFor) && (
            <div className="mt-8">
              <div
                className="rounded-2xl p-6 md:p-8 border border-white/5"
                style={{ background: '#131923' }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/15">
                    <i className="ri-file-list-3-line text-accent text-lg" />
                  </div>
                  <h3 className="font-poppins font-bold text-lg text-white">Dosage & Usage</h3>
                </div>
                <p className="text-text-muted text-sm leading-relaxed">
                  {product.dosage}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Related Products */}
      {related.length > 0 && (
        <section
          ref={ref as React.RefObject<HTMLElement>}
          className="py-16 px-4 md:px-8"
          style={{ background: 'linear-gradient(180deg, #0E1218 0%, #101828 100%)' }}
        >
          <div className="max-w-7xl mx-auto">
            <div className={`mb-10 reveal ${isVisible ? 'visible' : ''}`}>
              <p className="text-accent text-xs font-semibold tracking-[3px] uppercase mb-3">
                You May Also Like
              </p>
              <h2 className="font-poppins font-bold text-2xl md:text-3xl text-white">
                Related Products
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((rel, i) => (
                <Link
                  key={rel.id}
                  to={`/product/${rel.id}`}
                  className={`group rounded-2xl overflow-hidden border border-white/5 transition-all duration-300 hover:border-accent/30 reveal reveal-delay-${i + 1} ${isVisible ? 'visible' : ''}`}
                  style={{ background: '#1A2130' }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden" style={{ background: '#F5F5F0' }}>
                    <img
                      src={rel.image}
                      alt={rel.name}
                      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <p className="text-accent text-xs font-medium tracking-wider uppercase mb-1.5">
                      {rel.category}
                    </p>
                    <h3 className="font-poppins font-bold text-base text-white mb-2 group-hover:text-accent transition-colors">
                      {rel.name}
                    </h3>
                    <p className="text-text-muted text-sm line-clamp-2">{rel.benefit}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-poppins font-bold text-2xl md:text-3xl text-white mb-4">
            Need Help Choosing?
          </h2>
          <p className="text-text-muted mb-8 max-w-lg mx-auto">
            Our team is ready to help you find the perfect solution for your livestock. Reach out on WhatsApp for personalized recommendations.
          </p>
          <a
            href="https://wa.me/923007321011"
            target="_blank"
            rel="noopener noreferrer"
            className="wa-btn inline-flex items-center gap-3 px-8 py-4 rounded-full text-base font-semibold text-white cursor-pointer whitespace-nowrap red-glow"
          >
            <i className="ri-whatsapp-line text-xl" />
            Chat on WhatsApp
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}